import axios from 'axios'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import React, { Fragment } from 'react'
import { call, put, select } from 'redux-saga/effects'
import qs from 'qs'
import { saveAs } from 'file-saver'
import { apiToken } from '../account/selectors'
import { apiBasePath } from '../app/selectors'
import { hideLoading, showLoading } from '../app/actions'
import { checkCognitoSession } from '../clients/userPool'
import { NS_NULL_VALUE } from '../app/constants'
import LEGACY_PRODUCTS from './enum/legacyProducts'

let apiCount = 0

dayjs.extend(utc)

export function getQueryParams(location = window.location) {
  const queryParams = location.hash ? location.hash.split('?')[1] : location.search
  return qs.parse(queryParams, { ignoreQueryPrefix: true })
}

export function* api(inputOptions) {
  const session = checkCognitoSession()
  const idToken = session ? session.getIdToken()
    .getJwtToken() : null

  const params = getQueryParams(window.location)

  const options = {
    method: inputOptions.method || 'GET',
    params: inputOptions.params || {
      // Check for new API feature flag
      ...params.useMinWageApi && { useMinWageApi: params.useMinWageApi }
    },
    ...inputOptions
  }

  try {
    apiCount += 1

    if (!options.avoidGlobalLoading) {
      yield put(showLoading(apiCount))
    }

    const token = yield select(apiToken)
    const apiBase = yield select(apiBasePath)

    const headers = { 'Content-Type': 'application/json' }

    if (idToken) {
      headers.Authorization = `Bearer ${idToken}`
    } else {
      headers.token = token
    }

    // Manipulate data if doing a POST
    if (options.method.toLowerCase() === 'post') {
      headers['content-type'] = 'application/x-www-form-urlencoded'

      // Convert data to urlencoded format
      options.data = qs.stringify(options.data)
    }

    const axiosReturn = yield call(axios, {
      baseURL: apiBase,
      data: options.data ? options.data : {}, // If we send blank data the Content-Type header does not get set
      headers,
      method: options.method,
      params: options.params,
      responseType: 'json',
      validateStatus: () => true,
      url: options.api
    })

    if (axiosReturn.status !== 200) {
      // Check for errors here
      // TODO: Should be done with error codes, not message text
      if (axiosReturn.data.error.message === 'Account has not been authorized.') {
        sessionStorage.removeItem('token')
        sessionStorage.removeItem('accountData')
        window.location.reload()
      }
    }

    apiCount -= 1

    if (apiCount === 0) {
      yield put(hideLoading())
    } else {
      yield put(showLoading(apiCount))
    }
    return axiosReturn.data
  } catch (error) {
    apiCount -= 1
    return { error }
  }
}

export const callApi = async (options) => {
  const session = checkCognitoSession()
  const token = window.sessionStorage.getItem('token')
  const idToken = session ? session.getIdToken()
    .getJwtToken() : null
  const headers = {}
  headers['Content-Type'] = 'application/json'

  if (idToken) {
    headers.Authorization = `Bearer ${idToken}`
  } else {
    headers.token = token
  }

  const instance = axios.create({
    baseURL: options.baseURL ? options.baseURL : `${API_BASE_PATH}`,
    data: options.data ? options.data : {},
    headers,
    method: options.method ? options.method : 'GET',
    params: options.params,
    responseType: 'json',
    validateStatus: () => true,
    cancelToken: options.cancelToken
  })

  return instance.request(options.api).then((result) => result.data)
}

export const apiAsync = async (options) => {
  const session = checkCognitoSession()
  const token = window.sessionStorage.getItem('token')
  const idToken = session ? session.getIdToken()
    .getJwtToken() : null
  const headers = {}
  headers['Content-Type'] = 'application/json'

  if (idToken) {
    headers.Authorization = `Bearer ${idToken}`
  } else {
    headers.token = token
  }

  const config = {
    baseURL: options.baseURL ? options.baseURL : `${API_BASE_PATH}`,
    data: options.data ? options.data : {},
    headers,
    url: options.api,
    method: options.method ? options.method : 'GET',
    params: options.params,
    responseType: 'json',
    validateStatus: () => true,
    cancelToken: options.cancelToken
  }

  const response = await axios(config)
    .catch((responseError) => {
      if (axios.isCancel(responseError)) {
        console.log('request canceled')
      } else {
        console.error(responseError)
      }
    })

  if (response.status >= 400) {
    console.error(response)
    return {}
  }
  return response.data
}

export const formatCounty = (county) => {
  if (!county) {
    return null
  }

  if (county.toLowerCase()
    .slice(-6) === 'county') {
    return county
  }

  return `${county} County`
}

export const renderLegacyAddress = ({
  address1,
  address2,
  address3,
  city,
  state,
  zip,
  shipAttention
}) => {
  return (
    <p>
      <div className="text">{address1}</div>
      {address2 && address2 !== NS_NULL_VALUE ? (
        <div className="text">{address2}</div>
      ) : null}
      {address3 && address3 !== NS_NULL_VALUE ? (
        <div className="text">{address3}</div>
      ) : null}
      <div className="text">
        {`${city}, ${state} ${zip}`}
      </div>
      <div className="text">
        {shipAttention ? `Attn: ${shipAttention}` : null}
      </div>
    </p>
  )
}

/* Address from location services location details lambda is in this format:
"address": {
    "addressOne": "Riverfront Centre",
    "addressTwo": "314 Main Street",
    "city": "Red Wing",
    "stateCode": "MN",
    "zip": "55066"
},
 */
export const renderAddress = ({
  addressOne,
  addressTwo,
  addressThree,
  city,
  stateCode,
  zip,
  shipAttention
}) => {
  return (
    <Fragment>
      <div>{addressOne}</div>
      {/* TODO: Is NS_NULL_VALUE still relevant in the location services location details lambda?? */}
      {addressTwo && addressTwo !== NS_NULL_VALUE ? (
        <div>{addressTwo}</div>
      ) : null}
      {addressThree && addressThree !== NS_NULL_VALUE ? (
        <div>{addressThree}</div>
      ) : null}
      <div>
        {`${city}, ${stateCode} ${zip}`}
      </div>
      <div>
        {shipAttention ? `Attn: ${shipAttention}` : null}
      </div>
    </Fragment>
  )
}

export const capitalizeWords = (str) => {
  return str
    ? str.replace(/\w\S*/g, (txt) => txt.charAt(0)
      .toUpperCase() + txt.substr(1)
      .toLowerCase())
    : null
}

export const formatAddressInline = ({
  address1,
  address2,
  address3,
  city,
  county,
  state,
  zip
}) => {
  return (
    <Fragment>
      {address1}
      {address2 && address2 !== NS_NULL_VALUE
        ? `${address2} `
        : null}
      {address3 && address3 !== NS_NULL_VALUE
        ? `${address3} `
        : null}
      {city},
      {county ? formatCounty(county) : county}
      {state}
      {zip}
    </Fragment>
  )
}

export const formatAddressInlineAsText = (location) => {
  let street = location.address1 ? location.address1 : ''
  if (location.address2 && location.address2 !== NS_NULL_VALUE) {
    street += ` ${location.address2}`
  }
  if (location.address3 && location.address3 !== NS_NULL_VALUE) {
    street += ` ${location.address3}`
  }
  return `${street} ${location.city ? `${location.city}, ` : ''} ${
    location.state ? location.state : ''
  } ${location.zip ? location.zip : ''} ${
    location.country ? location.country : ''
  }`
}

export const addressStringByType = (type, addresses) => {
  const addressObject = addresses.find((address) => address.type && (address.type.toLowerCase() === type.toLowerCase()))
  return formatAddressInlineAsText({
    address1: capitalizeWords(addressObject.address1),
    address2: capitalizeWords(addressObject.address2),
    address3: capitalizeWords(addressObject.address3),
    city: capitalizeWords(addressObject.city),
    state: addressObject.state,
    zip: addressObject.zip,
    county: capitalizeWords(addressObject.county),
    country: addressObject.country
  })
}

export const addressObjectByType = (type, addresses) => addresses.find((address) => address.type && (address.type.toLowerCase() === type.toLowerCase()))

export const formatDate = (date = new Date()) => {
  let formattedDate = ''
  if (date && dayjs(date).isValid()) {
    formattedDate = dayjs.utc(date).format('MM/DD/YYYY')
  }
  return formattedDate
}

export const formatCurrency = (value, defaultValue = '') => {
  if (value === null || value === undefined) {
    return defaultValue
  }
  const newValue = value.toFixed(Math.max(2, (value.toString()
    .split('.')[1] || []).length))
  return `$${newValue}`
}

export const formatWageText = (wage) => (wage ? formatCurrency(wage.amount) : 'No Change')
export const formatTippedWageText = (wage) => (wage ? formatCurrency(wage.amount) : 'See location details')

export const handlePagination = (data, rowsPerPage, pageNumber) => {
  const getStartItem = () => (Math.ceil(pageNumber * rowsPerPage) <= data.length
    ? Math.ceil(pageNumber * rowsPerPage) - rowsPerPage
    : Math.ceil((pageNumber - 1) * rowsPerPage))

  const startItem = pageNumber === 1 ? 0 : getStartItem()

  const lastItem = startItem + rowsPerPage <= data.length
    ? startItem + rowsPerPage
    : data.length

  return data.slice(startItem, lastItem)
}

export const formatLink = (url) => {
  return url.match(/^(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:\/\n]+)/im)[0]
}

export const formatNumber = (number) => {
  if (typeof number === 'undefined') {
    return null
  }
  if (number === null) {
    return null
  }

  if (number === '') {
    return null
  }

  return parseFloat(number)
}

const shipMethods = [
  {
    carrierCode: 'dhl',
    urlPattern: 'http://www.dhl.com/en/express/tracking.html?AWB=$1&brand=DHL'
  },
  {
    carrierCode: 'fedex',
    urlPattern:
      'https://www.fedex.com/apps/fedextrack/?action=track&trackingnumber=$1&cntry_code=us'
  },
  {
    carrierCode: 'usps',
    urlPattern: 'https://tools.usps.com/go/TrackConfirmAction.action?tLabels=$1'
  },
  {
    carrierCode: 'ups',
    urlPattern:
      'https://wwwapps.ups.com/WebTracking/track?trackNums=$1&track.x=track'
  }
]

export const getTrackingLinks = (carrierCode, trackingNumber) => {
  if (trackingNumber === NS_NULL_VALUE) {
    return { number: trackingNumber }
  }
  const methodIndex = shipMethods.findIndex((x) => x.carrierCode === carrierCode)
  if (methodIndex === -1) {
    return { number: trackingNumber }
  }
  if (methodIndex === 2 && (trackingNumber.includes('seachange') || trackingNumber.includes('wallace'))) {
    let trackingLink = trackingNumber
    // Since March 2025 all tracking numbers will be stored with the https:// prefix in the database
    // This is to make sure that all tracking numbers before that have the prefix
    const reg = /https:\/\//
    if (!reg.test(trackingLink)) { trackingLink = `https://${trackingLink}` }
    return {
      number: trackingNumber,
      link: trackingLink
    }
  }
  return {
    number: trackingNumber,
    link: shipMethods[methodIndex].urlPattern.replace('$1', trackingNumber)
  }
}

export const getSalesforceOrderTrackingLinks = (carrier, trackingNumber) => {
  if (trackingNumber === null) {
    return { number: trackingNumber, link: '' }
  }

  const carrierLower = carrier.toLowerCase()
  const methodIndex = shipMethods.findIndex((method) => carrierLower.includes(method.carrierCode))

  if (trackingNumber.includes('seachange') || trackingNumber.includes('wallace')) {
    let trackingLink = trackingNumber
    // Since March 2025 all tracking numbers will be stored with the https:// prefix in the database
    // This is to make sure that all tracking numbers before that have the prefix
    const reg = /https:\/\//
    if (!reg.test(trackingLink)) { trackingLink = `https://${trackingLink}` }
    return {
      number: trackingNumber,
      link: trackingLink
    }
  }

  if (methodIndex === -1) {
    return { number: `${trackingNumber} (Unknown Carrier)`, link: '' }
  }

  const link = shipMethods[methodIndex].urlPattern.replace('$1', trackingNumber)

  return { number: trackingNumber, link }
}

export const stateList = [
  {
    name: 'Alabama',
    state: 'AL',
    country: 'US'
  },
  {
    name: 'Alberta',
    state: 'AB',
    country: 'CA'
  },
  {
    name: 'Alaska',
    state: 'AK',
    country: 'US'
  },
  {
    name: 'Arkansas',
    state: 'AR',
    country: 'US'
  },
  {
    name: 'Arizona',
    state: 'AZ',
    country: 'US'
  },
  {
    name: 'British Columbia',
    state: 'BC',
    country: 'CA'
  },
  {
    name: 'California',
    state: 'CA',
    country: 'US'
  },
  {
    name: 'Colorado',
    state: 'CO',
    country: 'US'
  },
  {
    name: 'Connecticut',
    state: 'CT',
    country: 'US'
  },
  {
    name: 'District of Columbia',
    state: 'DC',
    country: 'US'
  },
  {
    name: 'Delaware',
    state: 'DE',
    country: 'US'
  },
  {
    name: 'Federal',
    state: 'FE',
    country: 'US'
  },
  {
    name: 'Florida',
    state: 'FL',
    country: 'US'
  },
  {
    name: 'Georgia',
    state: 'GA',
    country: 'US'
  },
  {
    name: 'Guam',
    state: 'GU',
    country: 'US'
  },
  {
    name: 'Hawaii',
    state: 'HI',
    country: 'US'
  },
  {
    name: 'Iowa',
    state: 'IA',
    country: 'US'
  },
  {
    name: 'Idaho',
    state: 'ID',
    country: 'US'
  },
  {
    name: 'Illinois',
    state: 'IL',
    country: 'US'
  },
  {
    name: 'Indiana',
    state: 'IN',
    country: 'US'
  },
  {
    name: 'Kansas',
    state: 'KS',
    country: 'US'
  },
  {
    name: 'Kentucky',
    state: 'KY',
    country: 'US'
  },
  {
    name: 'Louisiana',
    state: 'LA',
    country: 'US'
  },
  {
    name: 'Manitoba',
    state: 'MB',
    country: 'CA'
  },
  {
    name: 'Maryland',
    state: 'MD',
    country: 'US'
  },
  {
    name: 'Massachusetts',
    state: 'MA',
    country: 'US'
  },
  {
    name: 'Maine',
    state: 'ME',
    country: 'US'
  },
  {
    name: 'Michigan',
    state: 'MI',
    country: 'US'
  },
  {
    name: 'Minnesota',
    state: 'MN',
    country: 'US'
  },
  {
    name: 'Missouri',
    state: 'MO',
    country: 'US'
  },
  {
    name: 'Mississippi',
    state: 'MS',
    country: 'US'
  },
  {
    name: 'Montana',
    state: 'MT',
    country: 'US'
  },
  {
    name: 'New Brunswick',
    state: 'NB',
    country: 'CA'
  },
  {
    name: 'North Carolina',
    state: 'NC',
    country: 'US'
  },
  {
    name: 'North Dakota',
    state: 'ND',
    country: 'US'
  },
  {
    name: 'Northern Mariana Islands',
    state: 'MP',
    country: 'US'
  },
  {
    name: 'Nebraska',
    state: 'NE',
    country: 'US'
  },
  {
    name: 'New Hampshire',
    state: 'NH',
    country: 'US'
  },
  {
    name: 'New Jersey',
    state: 'NJ',
    country: 'US'
  },
  {
    name: 'Newfoundland and Labrador',
    state: 'NL',
    country: 'CA'
  },
  {
    name: 'New Mexico',
    state: 'NM',
    country: 'US'
  },
  {
    name: 'Nova Scotia',
    state: 'NS',
    country: 'CA'
  },
  {
    name: 'Nevada',
    state: 'NV',
    country: 'US'
  },
  {
    name: 'New York',
    state: 'NY',
    country: 'US'
  },
  {
    name: 'Northwest Territories',
    state: 'NT',
    country: 'CA'
  },
  {
    name: 'Nunavut',
    state: 'NU',
    country: 'CA'
  },
  {
    name: 'Ohio',
    state: 'OH',
    country: 'US'
  },
  {
    name: 'Oklahoma',
    state: 'OK',
    country: 'US'
  },
  {
    name: 'Ontario',
    state: 'ON',
    country: 'CA'
  },
  {
    name: 'Oregon',
    state: 'OR',
    country: 'US'
  },
  {
    name: 'Pennsylvania',
    state: 'PA',
    country: 'US'
  },
  {
    name: 'Prince Edward Island',
    state: 'PE',
    country: 'CA'
  },
  {
    name: 'Puerto Rico',
    state: 'PR',
    country: 'US'
  },
  {
    name: 'Québec',
    state: 'QC',
    country: 'CA'
  },
  {
    name: 'Rhode Island',
    state: 'RI',
    country: 'US'
  },
  {
    name: 'South Carolina',
    state: 'SC',
    country: 'US'
  },
  {
    name: 'South Dakota',
    state: 'SD',
    country: 'US'
  },
  {
    name: 'Saskatchewan',
    state: 'SK',
    country: 'CA'
  },
  {
    name: 'Tennessee',
    state: 'TN',
    country: 'US'
  },
  {
    name: 'Texas',
    state: 'TX',
    country: 'US'
  },
  {
    name: 'U.S. Virgin Islands',
    state: 'VI',
    country: 'US'
  },
  {
    name: 'Utah',
    state: 'UT',
    country: 'US'
  },
  {
    name: 'Virginia',
    state: 'VA',
    country: 'US'
  },
  {
    name: 'Vermont',
    state: 'VT',
    country: 'US'
  },
  {
    name: 'Washington',
    state: 'WA',
    country: 'US'
  },
  {
    name: 'Wisconsin',
    state: 'WI',
    country: 'US'
  },
  {
    name: 'West Virginia',
    state: 'WV',
    country: 'US'
  },
  {
    name: 'Wyoming',
    state: 'WY',
    country: 'US'
  },
  {
    name: 'Yukon',
    state: 'YT',
    country: 'CA'
  }
].map((locale) => {
  const localeWithPrograms = locale

  if (['US'].includes(localeWithPrograms.country)) {
    localeWithPrograms.programs = [LEGACY_PRODUCTS.POSTINGS, LEGACY_PRODUCTS.MINWAGE, LEGACY_PRODUCTS.PAID_LEAVE]
  } else {
    localeWithPrograms.programs = [LEGACY_PRODUCTS.POSTINGS]
  }

  return localeWithPrograms
})
  .sort((a, b) => {
    if (a.name < b.name) return -1
    if (a.name > b.name) return 1
    return 0
  })

export const filterStateList = (product) => (
  stateList.filter((state) => state.programs && state.programs.includes(product))
)

export const statesByCode = (() => {
  const output = {}
  stateList.map((item) => {
    output[item.state] = item
  })
  return output
})()

export const getStateByCode = (jurisdictionCode) => stateList
  .find((jurisdiction) => jurisdiction.state === jurisdictionCode) || {
  name: '',
  state: '',
  country: ''
}

export const isEmptyObject = (obj) => {
  if (obj) {
    return Object.getOwnPropertyNames(obj).length === 0
  }
  return false
}

export const stateNameByCode = (code) => statesByCode[code] && statesByCode[code].name

export const copyObject = (object) => JSON.parse(JSON.stringify(object))

export const downloadFile = (link) => window.location = link

export const elementScroll = (containerId, tag) => {
  const container = document.getElementById(containerId)
  const elem = container.querySelectorAll(tag)[0]
  if (elem) {
    const current = elem.offsetTop - container.offsetTop

    container.scrollTo({
      top: current,
      behavior: 'smooth'
    })
  }
  return elem
}

export const wrapInQuotes = (element) => {
  return element ? `\"${element}\"` : ''
}

export const getCompanyObject = async (selectedCompanyId) => {
  const companyId = selectedCompanyId || 0
  return apiAsync({
    api: `/legacy/identity?company-id=${companyId}`,
    method: 'GET'
  })
}

export const exportCsv = (csvRows, csvFilename) => {
  try {
    if (csvRows.length) {
      const csvData = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8' })
      saveAs(csvData, csvFilename)
    } else {
      window.alert('No valid CSV data found!')
    }
  } catch (error) {
    console.error('Error while generating csv file ', error)
  }
}

export const convertNbspToSpaces = (text) => text && text.replace(/&nbsp;/gm, ' ')
export const stripLineBreaks = (text) => text && text.replace(/[\r\n]/gm, '')

export const stripMarkdown = (markDown) => {
  return (!markDown)
    ? ''
    : markDown
      // Remove horizontal rules
      .replace(/^([\s\t]*)([\*\-\+]|\d+\.)\s+/gm, '$1')
      // remove headers
      .replace(/\n={2,}/g, '\n')
      // Fenced codeblocks
      .replace(/~{3}.*\n/g, '')
      // Strikethrough
      .replace(/~~/g, '')
      // Fenced codeblocks
      .replace(/`{3}.*\n/g, '')
      // Remove HTML tags
      .replace(/<[^>]*>/g, '')
      // Remove setext-style headers
      .replace(/^[=\-]{2,}\s*$/g, '')
      // Remove footnotes?
      .replace(/\[\^.+?\](\: .*?$)?/g, '')
      .replace(/\s{0,2}\[.*?\]: .*?$/g, '')
      // Remove images
      .replace(/\!\[(.*?)\][\[\(].*?[\]\)]/g, '')
      // Remove inline links
      .replace(/\[(.*?)\][\[\(].*?[\]\)]/g, '$1')
      // Remove blockquotes
      .replace(/^\s{0,3}>\s?/g, '')
      // Remove reference-style links?
      .replace(/^\s{1,2}\[(.*?)\]: (\S+)( ".*?")?\s*$/g, '')
      // Remove atx-style headers
      .replace(/^(\n)?\s{0,}#{1,6}\s+| {0,}(\n)?\s{0,}#{0,} {0,}(\n)?\s{0,}$/gm, '$1$2$3')
      // Remove emphasis (repeat the line to remove double emphasis)
      .replace(/([\*_]{1,3})(\S.*?\S{0,1})\1/g, '$2')
      .replace(/([\*_]{1,3})(\S.*?\S{0,1})\1/g, '$2')
      // Remove code blocks
      .replace(/(`{3,})(.*?)\1/gm, '$2')
      // Remove inline code
      .replace(/`(.+?)`/g, '$1')
}

export const wrapStrippedMarkDownInQuotes = (element) => {
  return `\"${stripMarkdown(element)}\"`
}

export const filterLocales = (product, facets) => {
  const filteredStateList = filterStateList(product)
  const states = filteredStateList && filteredStateList.length > 0 ? filteredStateList : stateList

  // facets look like this: [{facet: 'US~ID', count: 65}, ...]
  const facetedLocales = facets && facets.length > 0
    ? facets.map((entry) => {
      const facetKeys = entry.facet.split('~')
      const locale = states.find((state) => state.state === facetKeys[1] && state.country === facetKeys[0])
      return {
        locale,
        entry
      }
    })
      .filter((location) => location.locale)
    : states.map((locale) => ({
      locale,
      facet: `${locale.country}~${locale.state}`
    }))

  return facetedLocales.sort((a, b) => {
    if (a.locale.name < b.locale.name) return -1
    if (a.locale.name > b.locale.name) return 1
    return 0
  })
}
